/* ==========================================================================
   fx-slideshow.js — Fox-Rings hero slideshow
   Native scroll-snap. No Slick, no jQuery.
   Autoplay pauses on hover, on focus, when the tab is hidden, when the
   slideshow scrolls out of view, and when the visitor prefers reduced motion.
   ========================================================================== */
class FxSlideshow extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-fx-track]');
    this.slides = Array.from(this.querySelectorAll('[data-fx-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-fx-dot]'));
    if (!this.track || this.slides.length < 2) return;

    this.index = 0;
    this.paused = false;
    this.timer = null;
    this.inView = true;

    this.reduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    var prev = this.querySelector('[data-fx-prev]');
    var next = this.querySelector('[data-fx-next]');
    if (prev) prev.addEventListener('click', () => this.go(this.index - 1));
    if (next) next.addEventListener('click', () => this.go(this.index + 1));

    this.dots.forEach((d) =>
      d.addEventListener('click', () => this.go(parseInt(d.dataset.fxDot, 10)))
    );

    this.pauseBtn = this.querySelector('[data-fx-pause]');
    if (this.pauseBtn) {
      this.pauseBtn.addEventListener('click', () => this.togglePause());
    }

    // keep the active dot in sync when the user swipes
    let settle = null;
    this.track.addEventListener(
      'scroll',
      () => {
        clearTimeout(settle);
        settle = setTimeout(() => this.syncFromScroll(), 90);
      },
      { passive: true }
    );

    // keyboard
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.index - 1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.index + 1); }
    });

    // pause while the visitor is interacting
    ['mouseenter', 'focusin', 'touchstart'].forEach((ev) =>
      this.addEventListener(ev, () => this.stop(), { passive: true })
    );
    ['mouseleave', 'focusout'].forEach((ev) =>
      this.addEventListener(ev, () => { if (!this.paused) this.start(); })
    );

    document.addEventListener('visibilitychange', () => {
      document.hidden ? this.stop() : (this.paused ? null : this.start());
    });

    // don't animate a slideshow nobody is looking at
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          this.inView = entries[0].isIntersecting;
          this.inView && !this.paused ? this.start() : this.stop();
        },
        { threshold: 0.25 }
      ).observe(this);
    }

    this.start();
  }

  disconnectedCallback() { this.stop(); }

  get delay() {
    return parseInt(this.dataset.autoplay, 10) || 0;
  }

  start() {
    if (!this.delay || this.reduced || !this.inView) return;
    this.stop();
    this.timer = setInterval(() => this.go(this.index + 1), this.delay);
  }

  stop() {
    if (this.timer) { clearInterval(this.timer); this.timer = null; }
  }

  togglePause() {
    this.paused = !this.paused;
    this.paused ? this.stop() : this.start();
    if (this.pauseBtn) {
      this.pauseBtn.setAttribute(
        'aria-label',
        this.paused ? 'Play slideshow' : 'Pause slideshow'
      );
      var icon = this.pauseBtn.querySelector('[data-fx-pause-icon]');
      if (icon) icon.innerHTML = this.paused ? '&#9654;' : '&#10073;&#10073;';
    }
  }

  go(i) {
    var n = this.slides.length;
    this.index = ((i % n) + n) % n;
    this.track.scrollTo({
      left: this.slides[this.index].offsetLeft,
      behavior: this.reduced ? 'auto' : 'smooth'
    });
    this.paint();
  }

  syncFromScroll() {
    var pos = this.track.scrollLeft;
    var closest = 0;
    var best = Infinity;
    this.slides.forEach((s, i) => {
      var d = Math.abs(s.offsetLeft - pos);
      if (d < best) { best = d; closest = i; }
    });
    if (closest !== this.index) { this.index = closest; this.paint(); }
  }

  paint() {
    this.dots.forEach((d, i) => {
      var on = i === this.index;
      d.classList.toggle('is-active', on);
      d.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  }
}

if (!customElements.get('fx-slideshow')) {
  customElements.define('fx-slideshow', FxSlideshow);
}
